<script setup lang="ts">
import 'altcha'
import { ref } from 'vue'

/**
 * The bot check, with nothing to look at.
 *
 * It asks nothing of the person: the browser works a small puzzle as the page
 * loads, so the box that showed "Verified" and the vendor's name only took up
 * room. It shows itself only when the browser could not finish, since a form
 * waiting on it would otherwise refuse to send with no reason given.
 */
const payload = defineModel<string>({ default: '' })

const widget = ref<HTMLElementTagNameMap['altcha-widget'] | null>(null)
const failed = ref(false)

function onStateChange(event: Event) {
  const detail = (event as CustomEvent<{ payload?: string, state: string }>).detail

  payload.value = detail.state === 'verified' ? (detail.payload ?? '') : ''
  failed.value = detail.state === 'error'

  // A solution left too long goes stale; a fresh one is worked out unseen.
  if (detail.state === 'expired') void widget.value?.verify()
}

// Each solution counts once, so a form the server sent back needs a fresh one.
function reset() {
  payload.value = ''
  failed.value = false
  widget.value?.reset()
  void widget.value?.verify()
}

// For a form sent before the puzzle was done: wait for it rather than refuse.
async function ensure(): Promise<string> {
  if (!payload.value) await widget.value?.verify()

  return payload.value
}

defineExpose({ reset, ensure })
</script>

<template>
  <altcha-widget
    ref="widget"
    auto="onload"
    display="invisible"
    challenge="/api/altcha-challenge"
    @statechange="onStateChange"
  />
  <p v-if="failed" class="text-sm text-danger" role="alert">
    We could not check this browser.
    <button type="button" class="font-semibold underline underline-offset-2" @click="reset">Try again</button>
  </p>
</template>
